import { Alert, AlertTitle, Box, Button, Container, TextField, Typography } from '@mui/material'
import { Formik } from 'formik'
import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useDispatch } from 'react-redux'
import { Link, useSearchParams } from 'react-router-dom'
import * as yup from 'yup'
import { AppBar } from '../../components'
import { updateSnackbarState } from '../../redux'
import { AccountApi, ResetPasswordConfirmDto } from '../../services/Swagger/Services'
import { Colors } from '../../theme'
import { useTranslation } from 'react-i18next'

const loginSchema = yup.object({
  email: yup.string().email().required(),
  token: yup.string().required(),
  password: yup.string().required(),
  passwordConfirmation: yup.string().oneOf([yup.ref('password')], 'Passwords must match'),
})

function ResetPassword() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const dispatch = useDispatch<any>()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [loginModel] = useState<ResetPasswordConfirmDto & { passwordConfirmation: string }>({
    email: searchParams.get('email') ?? '',
    password: '',
    passwordConfirmation: '',
    token: searchParams.get('token') ?? '',
  })
  const onLoginSubmit = async (values: ResetPasswordConfirmDto) => {
    setIsSubmitting(true)
    try {
      await new AccountApi().apiAccountsResetpwConfirmPost(values)
      setIsSuccess(true)
    } catch (error: any) {
      dispatch(
        updateSnackbarState({
          visible: true,
          message: 'Please try again!',
          type: 'error',
        } as any),
      )
    }
    setIsSubmitting(false)
  }
  return (
    <Box>
      <Helmet>
        <title>Cykelmakker - {t('resetPassword')}</title>
      </Helmet>
      <AppBar title={t('resetPassword')} />
      <Container maxWidth={'sm'}>
        <Box sx={{ my: 12 }}>
          <Typography variant='h3' align='center'>
            {t('resetPassword')}
          </Typography>
        </Box>
        <Formik initialValues={loginModel} onSubmit={onLoginSubmit} validationSchema={loginSchema}>
          {({ handleChange, handleSubmit, values, errors }) => (
            <>
              {!isSuccess && (
                <>
                  <Box sx={{ mb: 6 }}>
                    <Typography variant='caption'>{t('email')}</Typography>
                    <TextField
                      value={values.email}
                      fullWidth
                      variant='outlined'
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Box>
                  <Box sx={{ mb: 8 }}>
                    <Typography variant='caption'>{t('password')}</Typography>
                    <TextField
                      value={values.password}
                      onChange={handleChange('password')}
                      fullWidth
                      type='password'
                      variant='outlined'
                    />
                    {errors.password && (
                      <Typography className='textError'>{errors.password}</Typography>
                    )}
                  </Box>
                  <Box sx={{ mb: 8 }}>
                    <Typography variant='caption'>{t('password')}</Typography>
                    <TextField
                      value={values.passwordConfirmation}
                      onChange={handleChange('passwordConfirmation')}
                      fullWidth
                      type='password'
                      variant='outlined'
                    />
                    {errors.passwordConfirmation && (
                      <Typography className='textError'>{errors.passwordConfirmation}</Typography>
                    )}
                  </Box>
                  <Box sx={{ mb: 6 }}>
                    <Button
                      fullWidth
                      disabled={isSubmitting}
                      variant='contained'
                      onClick={() => handleSubmit()}
                    >
                      {t('resetPassword')}
                    </Button>
                  </Box>
                </>
              )}
              {isSuccess && (
                <>
                  <Alert severity='success' sx={{ mb: 8 }}>
                    <AlertTitle>{t('forgotSucessTitle')}</AlertTitle>
                    {t('signUpSucessDesc')}
                  </Alert>
                  <Typography sx={{ color: Colors.primary, mb: 8 }} align='center'>
                    <Link to='/auth/login'>{t('backToLogin')}</Link>
                  </Typography>
                </>
              )}
            </>
          )}
        </Formik>
      </Container>
    </Box>
  )
}

export default ResetPassword
